/**
 * Planningsgrid-cel (Epic C-3 — Planbord & indelen, Story C-3.3, UX-C1)
 *
 * Renders one employee's ochtend+middag for one date, layering the
 * taak-indeling ("ingedeeld") op de bestaande aanwezigheidskalender-taal.
 * Hergebruikt UITSLUITEND `aanwezigheidVisuals.ts`'s constantes/helpers
 * (geen kopie, geen nieuwe kleurconstantes voor bestaande statussen) — de
 * enige nieuwe rendering hier is de taakkleur-pasteltint + accentbalk.
 *
 * Optimistische UI (AC 4): een lokale override-state per dagdeel, zelfde
 * afweging als `QualificationCheckbox.tsx` (Story C-2.2) — geen React Query
 * `onMutate`-cache-patching. `AssignPopover` roept `onOptimisticSet`/
 * `onSaveFailed` aan; deze cel toont het optimisme totdat de achtergrond-
 * invalidatie (in `usePlanningBoard.ts`'s hooks) de echte data ververst.
 */

import { useState } from 'react';
import {
  VRIJ_COLOR,
  HOLIDAY_BG,
  HOLIDAY_BORDER,
  HOLIDAY_COLOR,
  ZIEK_STRIPE_LIGHT,
  ZIEK_STRIPE_DARK,
  ZIEK_TEXT,
  KANTOOR_BG,
  KANTOOR_ACCENT,
  KANTOOR_TEXT,
  THUIS_BG,
  THUIS_ACCENT,
  THUIS_TEXT,
  pastelTint,
  readableInk,
  LEAVE_PASTEL_AMOUNT,
  statusLabel,
} from '../../aanwezigheid/components/aanwezigheidVisuals';
import { EhboBadge } from '../../aanwezigheid/components/BhvBadge';
import { useToast } from '../../../components/Toast';
import { useUpsertAssignment, useDeleteAssignment } from '../hooks/usePlanningBoard';
import { AssignPopover } from './AssignPopover';
import type { BoardDagdeelEntry, PlanningDayPart, PlanningTaskDTO } from '@functieloonhuis/shared';

/** `undefined` = geen optimistische override (toon serverdata); `'deleted'` = optimistisch verwijderd; object = optimistisch ingedeeld. */
export type CellOverride = { taskId: number; taskName: string; taskColor: string | null; qualificationWarning: boolean } | 'deleted' | undefined;

function applyOverride(base: BoardDagdeelEntry, override: CellOverride): BoardDagdeelEntry {
  if (override === undefined) return base;
  if (override === 'deleted') return { ...base, assignment: null, conflict: false, qualificationWarning: false };
  return {
    ...base,
    assignment: { taskId: override.taskId, taskName: override.taskName, taskColor: override.taskColor, assignedByName: '', assignedAt: '' },
    conflict: false,
    qualificationWarning: override.qualificationWarning,
  };
}

/** Twee dagdelen zijn visueel identiek (samengevoegd tot één blok, AC 1) wanneer alles wat gerenderd wordt gelijk is. */
function sameCellVisual(a: BoardDagdeelEntry, b: BoardDagdeelEntry): boolean {
  return (
    a.status === b.status &&
    (a.location ?? null) === (b.location ?? null) &&
    a.blockReason === b.blockReason &&
    a.conflict === b.conflict &&
    a.qualificationWarning === b.qualificationWarning &&
    (a.assignment?.taskId ?? null) === (b.assignment?.taskId ?? null)
  );
}

function reasonLabel(reason: NonNullable<BoardDagdeelEntry['blockReason']>, holidayName: string | null): string {
  switch (reason) {
    case 'verlof':
      return 'verlof';
    case 'ziek':
      return 'ziek';
    case 'feestdag':
      return holidayName ? `feestdag: ${holidayName}` : 'feestdag';
    case 'niet_ingeroosterd':
      return 'niet ingeroosterd';
    case 'weekend':
      return 'weekend';
    case 'uit_dienst':
      return 'uit dienst';
    case 'niet_planbaar':
      return 'niet planbaar';
  }
}

/**
 * Exhaustieve per-dagdeel render — geblokkeerd (elke `blockReason`) wint
 * altijd van de "beschikbaar/ingedeeld"-tak (Story C-3.2's `deriveBlockReason`
 * bepaalt dit al server-side; hier alleen de bijbehorende VISUELE
 * behandeling, geen tweede workability-beslissing).
 */
function DagdeelContent({ entry, holidayName }: { entry: BoardDagdeelEntry; holidayName: string | null }) {
  if (entry.blockReason !== null) {
    switch (entry.blockReason) {
      case 'feestdag':
        return (
          <div className="flex items-center justify-center gap-1 h-full px-1" style={{ backgroundColor: HOLIDAY_BG, borderColor: HOLIDAY_BORDER }}>
            <span className="text-[9px] font-medium truncate" style={{ color: HOLIDAY_COLOR }}>
              {holidayName ?? 'Feestdag'}
            </span>
          </div>
        );
      case 'ziek':
        return (
          <div
            className="flex items-center justify-center gap-1 h-full px-1"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, ${ZIEK_STRIPE_LIGHT}, ${ZIEK_STRIPE_LIGHT} 4px, ${ZIEK_STRIPE_DARK} 4px, ${ZIEK_STRIPE_DARK} 8px)`,
            }}
          >
            <span className="text-[9px] font-medium truncate" style={{ color: ZIEK_TEXT }}>
              Ziek
            </span>
          </div>
        );
      case 'weekend':
        return (
          <div className="flex items-center justify-center h-full bg-[#F3F4F6]">
            <span className="text-[9px] text-gray-400">Weekend</span>
          </div>
        );
      case 'niet_ingeroosterd':
        // Bewust leeg wit vak — geen arcering, geen zichtbaar label (exact
        // aanwezigheidskalender-precedent, niet "fixen").
        return <div className="h-full bg-white" />;
      case 'uit_dienst':
        // Geen eigen celtype in de bron-UX (backend-open-besluit) — een
        // neutraal gedempt vak, onderscheidend van het lege "niet
        // ingeroosterd"-vak via een zichtbaar label.
        return (
          <div className="flex items-center justify-center h-full bg-[#F3F4F6]">
            <span className="text-[9px] text-gray-500">Uit dienst</span>
          </div>
        );
      case 'niet_planbaar':
        // Story D-1.4 (AD-35): de onderdrukte lege cel van een medewerker
        // buiten de verdeling — zelfde neutrale behandeling als uit_dienst.
        return (
          <div className="flex items-center justify-center h-full bg-[#F3F4F6]">
            <span className="text-[9px] text-gray-500">Niet planbaar</span>
          </div>
        );
      case 'verlof': {
        const bg = pastelTint(VRIJ_COLOR, LEAVE_PASTEL_AMOUNT);
        const ink = readableInk(VRIJ_COLOR);
        return (
          <div className="flex items-center justify-center h-full px-1" style={{ backgroundColor: bg }}>
            <span className="text-[9px] truncate" style={{ color: ink, fontWeight: 500 }}>
              Vrij
            </span>
          </div>
        );
      }
    }
  }

  if (entry.assignment) {
    const taskColor = entry.assignment.taskColor ?? '#334155';
    const bg = pastelTint(taskColor, LEAVE_PASTEL_AMOUNT);
    const ink = readableInk(taskColor);
    return (
      <div
        className="relative flex items-center justify-center h-full px-1 overflow-hidden"
        style={{ backgroundColor: bg, boxShadow: `inset 3px 0 0 ${taskColor}` }}
      >
        <span className="text-[9px] truncate" style={{ color: ink, fontWeight: 700 }}>
          {entry.assignment.taskName}
        </span>
        {entry.qualificationWarning && (
          // Code-review fix: top-LEFT (not top-right) — the EHBO-teken
          // (`EhboBadge`, rendered as a sibling one level up in
          // `DagdeelHalf`) already claims top-right; a BHV'er assigned to a
          // taak they're unqualified for would otherwise visually collide.
          <span
            data-testid="kwalificatie-warning-icoon"
            title="Kwalificatie ontbreekt"
            aria-label="Kwalificatie ontbreekt"
            className="absolute top-0.5 left-0.5 text-[10px] font-bold"
            style={{ color: '#B45309' }}
          >
            &#9650;
          </span>
        )}
      </div>
    );
  }

  const isKantoor = entry.location !== 'thuis';
  const bg = isKantoor ? KANTOOR_BG : THUIS_BG;
  const accent = isKantoor ? KANTOOR_ACCENT : THUIS_ACCENT;
  const textColor = isKantoor ? KANTOOR_TEXT : THUIS_TEXT;
  return (
    <div className="relative flex items-center justify-center h-full px-1 overflow-hidden" style={{ backgroundColor: bg, boxShadow: `inset 3px 0 0 ${accent}` }}>
      <span className="text-[9px] truncate" style={{ color: textColor, fontWeight: 700 }}>
        {isKantoor ? 'Kantoor' : 'Thuis'}
      </span>
    </div>
  );
}

function buildAriaLabel(employeeName: string, dayLabel: string, dagdeelLabel: string | null, entry: BoardDagdeelEntry): string {
  const prefix = dagdeelLabel ? `${dayLabel} ${dagdeelLabel}` : dayLabel;
  let label = `${employeeName}, ${prefix}: ${entry.assignment ? entry.assignment.taskName : statusLabel(entry.status)}`;
  if (entry.qualificationWarning) label += ', kwalificatie ontbreekt';
  if (entry.conflict) label += ', conflict';
  return label;
}

interface DagdeelHalfProps {
  entry: BoardDagdeelEntry;
  holidayName: string | null;
  isBhv: boolean;
  isPast: boolean;
  ariaLabel: string;
  onOpen: () => void;
  isSplit: boolean;
  rowIndex: number;
  colIndex: number;
}

function DagdeelHalf({ entry, holidayName, isBhv, isPast, ariaLabel, onOpen, isSplit, rowIndex, colIndex }: DagdeelHalfProps) {
  // Story C-4.3 (AC 3): een conflict-cel wordt nu ook klikbaar — dit maakt
  // de "minimale rode-rand-weergave" die C-3.3 al vooruitlopend leverde
  // functioneel compleet. `entry.blockReason !== null` blijft de basis-
  // blokkade (bv. een lege 'niet_ingeroosterd'-cel), maar een conflict-cel
  // heeft ALTIJD een bestaande indeling (`entry.assignment !== null`) die
  // opgeruimd moet kunnen worden — de klik opent dezelfde popover, die al
  // een "Indeling verwijderen"-actie toont zodra `currentAssignment` gezet is.
  const clickable = (entry.blockReason === null || entry.conflict) && !isPast;
  const title = entry.conflict
    ? `Conflict — ${entry.conflictCause ?? 'dit dagdeel is niet meer indeelbaar'}`
    : entry.blockReason !== null
      ? `Niet in te delen — ${reasonLabel(entry.blockReason, holidayName)}`
      : undefined;

  return (
    <button
      type="button"
      role="gridcell"
      aria-label={ariaLabel}
      aria-disabled={!clickable}
      title={title}
      data-row={rowIndex}
      data-col={colIndex}
      onClick={clickable ? onOpen : undefined}
      className={`relative block w-full text-left border-0 p-0 ${isSplit ? 'h-5' : 'h-10'} ${
        clickable ? 'cursor-pointer hover:ring-1 hover:ring-inset hover:ring-[#2F5A7A]' : 'cursor-default'
      } ${entry.conflict ? 'ring-2 ring-inset ring-[#D64545]' : ''}`}
    >
      <DagdeelContent entry={entry} holidayName={holidayName} />
      {isBhv && entry.blockReason === null && <EhboBadge />}
      {entry.conflict && (
        <span
          data-testid="conflict-icoon"
          title="Conflict — dit dagdeel is niet meer indeelbaar"
          aria-label="Conflict"
          className="absolute bottom-0.5 right-0.5 text-[10px] font-bold"
          style={{ color: '#D64545' }}
        >
          !
        </span>
      )}
    </button>
  );
}

export interface PlanningCellProps {
  employeeId: number;
  employeeName: string;
  isBhv: boolean;
  date: string;
  dayLabel: string;
  holidayName: string | null;
  morning: BoardDagdeelEntry;
  afternoon: BoardDagdeelEntry;
  tasks: PlanningTaskDTO[];
  unqualifiedTaskIds: number[];
  isPast: boolean;
  canManageTasks: boolean;
  rowIndex: number;
  colIndex: number;
  /** Story C-4.3 (AC 2) — kort gemarkeerd na "Aanpassen in planbord" vanuit het conflict-paneel. */
  highlighted?: boolean;
}

type ActivePart = 'ochtend' | 'middag' | 'merged' | null;

export function PlanningCell({
  employeeId,
  employeeName,
  isBhv,
  date,
  dayLabel,
  holidayName,
  morning,
  afternoon,
  tasks,
  unqualifiedTaskIds,
  isPast,
  canManageTasks,
  rowIndex,
  colIndex,
  highlighted = false,
}: PlanningCellProps) {
  const [overrides, setOverrides] = useState<{ ochtend?: CellOverride; middag?: CellOverride }>({});
  const [activePart, setActivePart] = useState<ActivePart>(null);
  const { showToast } = useToast();
  const upsert = useUpsertAssignment();
  const del = useDeleteAssignment();

  const effectiveMorning = applyOverride(morning, overrides.ochtend);
  const effectiveAfternoon = applyOverride(afternoon, overrides.middag);
  const merged = sameCellVisual(effectiveMorning, effectiveAfternoon);

  function handleOptimisticSet(parts: PlanningDayPart[], patch: CellOverride) {
    setOverrides((prev) => {
      const next = { ...prev };
      for (const part of parts) next[part] = patch;
      return next;
    });
  }

  function handleSaveFailed(parts: PlanningDayPart[]) {
    setOverrides((prev) => {
      const next = { ...prev };
      for (const part of parts) delete next[part];
      return next;
    });
    showToast('Indeling opslaan mislukt — probeer opnieuw.', 'error');
  }

  const targetParts: PlanningDayPart[] =
    activePart === 'merged' ? ['ochtend', 'middag'] : activePart === 'ochtend' ? ['ochtend'] : activePart === 'middag' ? ['middag'] : [];
  const currentAssignment =
    activePart === 'merged' || activePart === 'ochtend' ? effectiveMorning.assignment : activePart === 'middag' ? effectiveAfternoon.assignment : null;
  const dagdeelLabel = activePart === 'merged' ? 'hele dag' : activePart === 'ochtend' ? 'ochtend' : activePart === 'middag' ? 'middag' : '';
  // Story C-4.3 (AC 3) — "klik opent de popover in conflict-modus": een
  // conflict-cel opent dezelfde AssignPopover, met de C-4.2-oorzaaktekst als
  // extra notice (naast de al bestaande "Indeling verwijderen"-actie).
  const activeConflictEntry = activePart === 'merged' || activePart === 'ochtend' ? effectiveMorning : activePart === 'middag' ? effectiveAfternoon : null;
  const conflictNotice = activeConflictEntry?.conflict ? activeConflictEntry.conflictCause : null;

  return (
    <div
      data-testid={`planning-cell-${employeeId}-${date}`}
      className={`relative rounded border overflow-visible ${highlighted ? 'ring-2 ring-offset-1 ring-[#54949E]' : 'border-[#E2E8F0]'}`}
      style={{ height: '2.5rem' }}
    >
      {merged ? (
        <DagdeelHalf
          entry={effectiveMorning}
          holidayName={holidayName}
          isBhv={isBhv}
          isPast={isPast}
          isSplit={false}
          ariaLabel={buildAriaLabel(employeeName, dayLabel, null, effectiveMorning)}
          onOpen={() => setActivePart('merged')}
          rowIndex={rowIndex}
          colIndex={colIndex}
        />
      ) : (
        <div className="flex flex-col h-full">
          <DagdeelHalf
            entry={effectiveMorning}
            holidayName={holidayName}
            isBhv={isBhv}
            isPast={isPast}
            isSplit
            ariaLabel={buildAriaLabel(employeeName, dayLabel, 'ochtend', effectiveMorning)}
            onOpen={() => setActivePart('ochtend')}
            rowIndex={rowIndex}
            colIndex={colIndex}
          />
          <div className="border-t border-dashed border-[#E2E8F0]" />
          <DagdeelHalf
            entry={effectiveAfternoon}
            holidayName={holidayName}
            isBhv={isBhv}
            isPast={isPast}
            isSplit
            ariaLabel={buildAriaLabel(employeeName, dayLabel, 'middag', effectiveAfternoon)}
            onOpen={() => setActivePart('middag')}
            rowIndex={rowIndex}
            colIndex={colIndex}
          />
        </div>
      )}

      {activePart !== null && (
        <AssignPopover
          employeeFirstName={employeeName.split(' ')[0] ?? employeeName}
          dayLabel={dayLabel}
          dagdeelLabel={dagdeelLabel}
          canHeleDag={activePart === 'merged' && effectiveMorning.assignment === null}
          currentAssignment={currentAssignment}
          conflictNotice={conflictNotice}
          tasks={tasks}
          unqualifiedTaskIds={unqualifiedTaskIds}
          canManageTasks={canManageTasks}
          onClose={() => setActivePart(null)}
          onPick={async (task, heleDag) => {
            const parts: PlanningDayPart[] = heleDag ? ['ochtend', 'middag'] : targetParts;
            const qualificationWarning = unqualifiedTaskIds.includes(task.id);
            handleOptimisticSet(parts, { taskId: task.id, taskName: task.name, taskColor: task.color, qualificationWarning });
            setActivePart(null);
            try {
              await upsert.mutateAsync(parts.map((part) => ({ employeeId, date, dayPart: part, taskId: task.id })));
            } catch {
              handleSaveFailed(parts);
            }
          }}
          onRemove={async () => {
            const parts = targetParts;
            handleOptimisticSet(parts, 'deleted');
            setActivePart(null);
            try {
              await Promise.all(parts.map((part) => del.mutateAsync({ employeeId, date, dayPart: part })));
            } catch {
              handleSaveFailed(parts);
            }
          }}
        />
      )}
    </div>
  );
}
